import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTriageWorklist } from "@/hooks/useTriageSiteWorklist";
import { SimulatedLabel } from "@/components/triage/SimulatedLabel";
import {
  formatSeconds,
  hasOrderContext,
  orderRows,
  replayStatus,
  rowStatus,
  type TriageCaseNavState,
  type TriageRowStatus,
  type TriageWorklistRow,
} from "@/types/triageWorklist";

/** Replay pacing: a new study every 3 s, processing shown 20x faster than recorded. */
export const REPLAY = { arrivalSpacing: 3, speed: 20 } as const;

// State cues follow AGENTS.md "State encoding is non-hue": flagged = attention
// (border-strong + surface-2 lift + text label), not analyzed = dashed secondary,
// error = warn (hard error).
const CHIP = "text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border";

function StatusCell({ status }: { status: TriageRowStatus }) {
  switch (status.kind) {
    case "flagged":
      return (
        <span
          data-testid="triage-status-flagged"
          className={`${CHIP} border-border-strong text-fg bg-surface-2`}
        >
          Flagged
        </span>
      );
    case "no_flag":
      return <span className="text-[11px] text-fg-muted">No flag</span>;
    case "processing":
      return (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-muted">
          <span
            role="status"
            aria-label="Processing"
            className="h-2.5 w-2.5 animate-spin rounded-full border border-signal border-t-transparent"
          />
          Processing
          {status.elapsedSeconds != null && (
            <span className="font-mono tabular-nums">{formatSeconds(status.elapsedSeconds)}</span>
          )}
        </span>
      );
    case "not_analyzed":
      return (
        <span
          className={`${CHIP} border-dashed border-border-strong text-fg-muted`}
          title={status.message}
        >
          Not analyzed
        </span>
      );
    case "error":
      return (
        <span className={`${CHIP} border-warn/50 text-warn bg-warn-tint`} title={status.message}>
          Error
        </span>
      );
    case "not_routed":
      return <span className="text-[11px] text-fg-dim">Not routed</span>;
  }
}

function findingsText(status: TriageRowStatus): { text: string; muted: boolean } {
  switch (status.kind) {
    case "flagged":
      return { text: status.findings.join(", "), muted: false };
    case "no_flag":
      return { text: "No flag", muted: true };
    case "processing":
      return { text: "Awaiting device result", muted: true };
    case "not_analyzed":
    case "error":
      return { text: status.message, muted: true };
    case "not_routed":
      return { text: "—", muted: true };
  }
}

/**
 * A site's worklist with a triage device in it: every study, in arrival order.
 * Routing decides before the model which studies the device sees; flagged studies
 * move up, and nothing is moved down or removed.
 */
const TriageWorklistPage: React.FC = () => {
  const { worklistId } = useParams<{ worklistId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useTriageWorklist(token, worklistId);
  const [flaggedFirst, setFlaggedFirst] = useState(true);
  const [replayStart, setReplayStart] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (replayStart == null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [replayStart]);

  const replaySeconds = replayStart == null ? null : (now - replayStart) / 1000;
  const rows = useMemo(() => {
    const studies = data?.studies ?? [];
    const withStatus = studies.flatMap((row, index) => {
      const status =
        replaySeconds == null ? rowStatus(row) : replayStatus(row, index, replaySeconds, REPLAY);
      return status ? [{ row, index, status }] : [];
    });
    return orderRows(withStatus, (entry) => entry.status.kind === "flagged", flaggedFirst);
  }, [data, replaySeconds, flaggedFirst]);

  const total = data?.studies.length ?? 0;
  const showOrder = hasOrderContext(data?.studies ?? []);
  const replayDone =
    replaySeconds != null &&
    rows.length === total &&
    rows.every((entry) => entry.status.kind !== "processing");
  const counts = useMemo(() => {
    const c = { flagged: 0, routed: 0, notRouted: 0 };
    for (const entry of rows) {
      if (entry.status.kind === "not_routed") c.notRouted += 1;
      else c.routed += 1;
      if (entry.status.kind === "flagged") c.flagged += 1;
    }
    return c;
  }, [rows]);

  if (isLoading) return <p className="p-6 text-sm text-fg-muted">Loading worklist…</p>;
  if (isError || !data)
    return (
      <p role="alert" className="p-6 text-sm text-fg-muted">
        Could not load this triage worklist.
      </p>
    );

  // The case page steps Prev/Next through the rows in the order shown here.
  const openCase = (row: TriageWorklistRow) =>
    navigate(
      `/triage/${encodeURIComponent(data.meta.id)}/case/${encodeURIComponent(row.study_uid)}`,
      { state: { order: rows.map((entry) => entry.row.study_uid) } satisfies TriageCaseNavState },
    );

  return (
    <div className="flex h-full flex-col bg-bg text-fg">
      <header className="flex flex-wrap items-center gap-4 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">
            Site worklist · {data.meta.device_model}
          </div>
          <h1 className="text-sm font-medium">{data.meta.name}</h1>
        </div>
        <div className="font-mono text-[11px] text-fg-muted tabular-nums">
          {rows.length}/{total} studies · {counts.routed} routed · {counts.flagged} flagged ·{" "}
          {counts.notRouted} not routed
        </div>
        {showOrder && <SimulatedLabel>Location · priority simulated</SimulatedLabel>}
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-fg-muted">
            <input
              type="checkbox"
              checked={flaggedFirst}
              onChange={(e) => setFlaggedFirst(e.target.checked)}
            />
            Flagged first
          </label>
          <button
            type="button"
            onClick={() => {
              if (replayStart == null) {
                setNow(Date.now());
                setReplayStart(Date.now());
              } else setReplayStart(null);
            }}
            className="rounded-xs border border-border px-2.5 py-1 text-xs hover:border-border-strong"
          >
            {replayStart == null ? "Arrivals replay" : "Stop replay"}
          </button>
        </div>
      </header>
      {replayStart != null && (
        <div
          data-testid="replay-banner"
          className="border-b border-border bg-surface-2 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-muted"
        >
          Replay — recorded results · arrivals every {REPLAY.arrivalSpacing}s · processing shown{" "}
          {REPLAY.speed}× faster than recorded{replayDone ? " · complete" : ""}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 bg-bg">
            <tr className="border-b border-border font-mono text-[9px] uppercase tracking-[0.14em] text-fg-dim">
              <th className="px-4 py-2 font-normal">Arrived</th>
              <th className="px-2 py-2 font-normal">Exam</th>
              <th className="px-2 py-2 font-normal">Age</th>
              {showOrder && (
                <>
                  <th className="px-2 py-2 font-normal">Location</th>
                  <th className="px-2 py-2 font-normal">Priority</th>
                </>
              )}
              <th className="px-2 py-2 font-normal">Routing</th>
              <th className="px-2 py-2 font-normal">Status</th>
              <th className="px-2 py-2 font-normal">Findings</th>
              <th className="px-4 py-2 text-right font-normal">Case</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, index, status }) => {
              const findings = findingsText(status);
              const flagged = status.kind === "flagged";
              return (
                <tr
                  key={row.study_uid}
                  data-testid={`triage-row-${row.case_id}`}
                  onClick={() => openCase(row)}
                  className={`cursor-pointer border-b border-border text-xs transition-colors hover:bg-surface-2 ${
                    flagged
                      ? "border-l-2 border-l-border-strong bg-surface-2"
                      : "border-l-2 border-l-transparent"
                  }`}
                >
                  <td className="px-4 py-2.5 font-mono text-[11px] text-fg-dim tabular-nums">
                    #{index + 1}
                  </td>
                  <td className="px-2 py-2.5">{row.exam ?? "—"}</td>
                  <td className="px-2 py-2.5 font-mono text-[11px] tabular-nums">
                    {row.age_recorded ? (
                      row.age_years
                    ) : (
                      <span className="text-fg-dim">not recorded</span>
                    )}
                  </td>
                  {showOrder && (
                    <>
                      {/* Order context, not an AI state: plain text, no flag styling. */}
                      <td className="px-2 py-2.5 text-[11px] text-fg-muted">
                        {row.order_context?.location}
                      </td>
                      <td className="px-2 py-2.5 font-mono text-[11px] text-fg-muted">
                        {row.order_context?.priority}
                      </td>
                    </>
                  )}
                  <td className="px-2 py-2.5 text-[11px]">
                    {row.routing.routed ? (
                      <span className="text-fg-muted">Routed</span>
                    ) : (
                      <span className="text-fg-dim">{row.routing.reason}</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    <StatusCell status={status} />
                  </td>
                  <td className={`px-2 py-2.5 ${findings.muted ? "text-fg-dim" : "font-medium"}`}>
                    {findings.text}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-[11px] text-fg-dim">
                    {row.case_id}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <footer className="border-t border-border px-4 py-2 text-[10px] text-fg-dim">
        Routing applies the device's indications before the model runs (interim study-type rule;
        replaced by the study orchestrator, ENG-219). Studies outside the indications are never sent
        to the device.
      </footer>
    </div>
  );
};

export default TriageWorklistPage;
